// @ts-strict-ignore
import {
  CommunicationService,
  ConsentProviderInterface,
  eventsRepository,
} from "@identity/services";
import { delay, inject, injectWithTransform, singleton } from "tsyringe";
import { Consents } from "../types";
import { COOKIES, WINDOW } from "@identity/symbols";
import {
  Logger,
  LoggerTransformer,
  TopLevelDomainExtractor,
} from "@identity/utils";

interface UspDataMessage {
  version: number;
  uspString: string;
}

export enum USP_VALUES {
  yes = "Y",
  no = "N",
  na = "-",
}

@singleton()
export class UspApi implements ConsentProviderInterface {
  private readonly usPrivacyCookie = "usprivacy";
  private readonly USP_API_VERSION = 1;
  private readonly CCPA_TRACKING_ALLOWED = "1YN";
  private readonly queue = [];
  private readonly explicitNotice = USP_VALUES.yes;
  private readonly lspaSupport = USP_VALUES.no;

  constructor(
    @inject(COOKIES) private readonly cookiesManager: CookiesManager,
    @inject(WINDOW) private readonly window: Window,
    @injectWithTransform(Logger, LoggerTransformer, "USP")
    private logger: Logger,
    @inject(delay(() => CommunicationService))
    private readonly communicationService: CommunicationService,
  ) {
    this.communicationService.on(
      eventsRepository.UPDATE_USP_SIGNAL,
      ({ trackingConsent }) => {
        this.createPrivacyCookie(
          trackingConsent ? USP_VALUES.no : USP_VALUES.yes,
        );
      },
    );
  }

  isUspApiAvailable(): boolean {
    return this.window.__uspapi !== undefined;
  }

  installUspApi(): void {
    this.window.__uspapi = this.uspApiMessage.bind(this);

    this.addLocatorFrame();
    this.addUspMessageListener();
    this.logger.info("__uspapi installed");
  }

  getPrivacyStringCookie(): string {
    return this.cookiesManager.get(this.usPrivacyCookie);
  }

  createPrivacyCookie(optOut = USP_VALUES.na) {
    const domain = this.window.location.hostname;
    const topLevelDomain = TopLevelDomainExtractor.extract(domain);
    const usPrivacy =
      optOut === USP_VALUES.na
        ? `${this.USP_API_VERSION}${USP_VALUES.na}${USP_VALUES.na}${USP_VALUES.na}`
        : `${this.USP_API_VERSION}${this.explicitNotice}${optOut}${this.lspaSupport}`;
    this.cookiesManager.set(this.usPrivacyCookie, usPrivacy, {
      domain: topLevelDomain,
      expires: 365,
    });
  }

  consentsUpdated(consent: string): Consents {
    return {
      gdprConsent: true,
      geoRequiresConsent: false,
      geoRequiresSignal: true,
      ccpaSignal: !consent.startsWith(this.CCPA_TRACKING_ALLOWED),
    };
  }

  private uspApiMessage(commandName, version, callback) {
    if (commandName === "ping") {
      callback({ uspapiLoaded: false }, true);
    } else if (commandName === "getUSPData") {
      callback(
        {
          version: this.USP_API_VERSION,
          uspString: this.getPrivacyStringCookie(),
        },
        true,
      );
    } else if (commandName === "getQueue") {
      callback(this.queue, true);
    } else {
      this.queue.push([commandName, version, callback]);
    }
  }

  private addLocatorFrame(): void {
    const name = "__uspapi";

    if (this.window.frames[name]) {
      return;
    }

    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () =>
        this.addLocatorFrame(),
      );
      return;
    }

    const iframe = document.createElement("iframe");

    iframe.name = name;
    iframe.style.display = "none";
    document.body.appendChild(iframe);
  }

  private addUspMessageListener(): void {
    this.window.addEventListener(
      "message",
      (event) => {
        const call = this.convertUspMessageData(event);
        if (call) {
          this.uspMessageHandler(call, event);
        }
      },
      false,
    );
  }

  private convertUspMessageData(event): unknown {
    try {
      const data =
        typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      return data.__uspapiCall;
    } catch (error) {
      void error;
    }
  }

  private uspMessageHandler(call, event): void {
    this.window.__uspapi(call.command, call.version, (returnValue, success) => {
      const returnMsg = {
        __uspapiReturn: {
          returnValue,
          success,
          callId: call.callId,
        },
      };
      event.source.postMessage(returnMsg, "*");
    });
  }

  subscribe(callback): void {
    this.window.__uspapi &&
      this.window.__uspapi(
        "getUSPData",
        this.USP_API_VERSION,
        (consent: UspDataMessage) => {
          if (consent?.uspString) {
            callback(this.consentsUpdated(consent.uspString));
          }
        },
      );
  }
}
